import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Star } from "lucide-react";
import { company, reviews } from "@/content/site";

const Stars = ({ value, label }) => (
  <span className="flex items-center gap-0.5" role="img" aria-label={label}>
    {[0, 1, 2, 3, 4].map((i) => {
      const fill = Math.max(0, Math.min(1, value - i));
      return (
        <span key={i} className="relative inline-block h-4 w-4">
          <Star aria-hidden="true" size={16} className="absolute inset-0 text-[#B8C5D1]" />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
            <Star aria-hidden="true" size={16} className="fill-current" />
          </span>
        </span>
      );
    })}
  </span>
);

const ReviewCard = ({ r, i }) => (
  <li className="review-card snap-start" data-testid={`review-card-${i}`} aria-roledescription="slide" aria-label={`Review ${i + 1} of ${reviews.length}`}>
    <article className="flex h-full flex-col rounded-[12px] border border-[#D9E1E8] bg-white p-7 shadow-[0_1px_2px_rgba(16,42,67,0.05),0_14px_34px_-22px_rgba(16,42,67,0.35)] md:p-9">
      {r.rating ? <Stars value={r.rating} label={`${r.rating} out of 5 stars`} /> : <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3E5468]">Google review</p>}
      <blockquote className="mt-5 flex-1 text-base leading-relaxed text-[#151A20] md:text-lg">“{r.text}”</blockquote>
      <footer className="mt-8 flex flex-wrap items-end justify-between gap-3 border-t border-[#EEF2F5] pt-5">
        <div>
          <p className="font-semibold text-[#151A20]">{r.name}</p>
          <p className="text-sm text-[#4A5866]">{r.date} · Google</p>
        </div>
        <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#102A43] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#102A43]">
          View on Google <ExternalLink aria-hidden="true" size={14} />
        </a>
      </footer>
    </article>
  </li>
);

export const ReviewsCarousel = () => {
  const track = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  const sync = useCallback(() => {
    const el = track.current;
    if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);

  useEffect(() => {
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [sync]);

  const go = (dir) => {
    const el = track.current;
    const card = el?.querySelector(".review-card");
    if (!el || !card) return;
    el.scrollBy({ left: dir * (card.getBoundingClientRect().width + 24), behavior: "smooth" });
  };

  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
  };

  return (
    <div data-testid="reviews-carousel">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-5">
        <div className="flex items-center gap-3 text-white">
          <Stars value={company.googleRating} label={`${company.googleRating} out of 5 average rating`} />
          <p className="text-sm text-white"><strong>{company.googleRating}</strong> overall rating on Google</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-glass btn-icon" onClick={() => go(-1)} disabled={edge.start} aria-label="Previous review" data-testid="reviews-prev-btn"><ChevronLeft aria-hidden="true" size={20} /></button>
          <button type="button" className="btn-glass btn-icon" onClick={() => go(1)} disabled={edge.end} aria-label="Next review" data-testid="reviews-next-btn"><ChevronRight aria-hidden="true" size={20} /></button>
        </div>
      </div>
      <ul
        ref={track}
        onScroll={sync}
        onKeyDown={onKey}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Customer reviews. Use the left and right arrow keys to move between reviews."
        className="reviews-track flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#102A43]"
        data-testid="reviews-track"
      >
        {reviews.map((r, i) => <ReviewCard key={`${r.name}-${i}`} r={r} i={i} />)}
      </ul>
      <p className="mt-6 text-sm text-[#E3EAF0]">Reviews appear in full as published on Alumicom's Google Business Profile.</p>
    </div>
  );
};
